// Main application entry point

import { initDatabase, saveDatabase, databaseExists } from "./db.js";
import * as router from "./router.js";
import { renderHomePage } from "./pages/home.js";
import { renderTemplatesPage } from "./pages/templates.js";
import { renderActiveWorkoutPage } from "./pages/active-workout.js";
import { renderHistoryPage } from "./pages/history.js";
import { renderExercisesPage } from "./pages/exercises.js";
import { renderSettingsPage } from "./pages/settings.js";
import { renderTemplateDetailPage } from "./pages/template-detail.js";
import { renderOnboardingPage } from "./pages/onboarding.js";
import { renderCardioEntryPage } from "./pages/cardio-entry.js";

// Global state
window.appState = {
  db: null,
  currentWorkout: null,
  settings: null,
};

// Register all app routes
function registerAppRoutes() {
  router.registerRoute("/", renderHomePage);
  router.registerRoute("/templates", renderTemplatesPage);
  router.registerRoute("/templates/:id", renderTemplateDetailPage);
  router.registerRoute("/workout/:id", renderActiveWorkoutPage);
  router.registerRoute("/history", renderHistoryPage);
  router.registerRoute("/exercises", renderExercisesPage);
  router.registerRoute("/settings", renderSettingsPage);
  router.registerRoute("/cardio-entry", renderCardioEntryPage);
}

// Initialize application
async function init() {
  try {
    // Show loading
    document.getElementById("loading").style.display = "flex";

    // Check if database exists
    console.log("Checking for existing database...");
    const hasDatabase = await databaseExists();

    if (!hasDatabase) {
      // No database exists - show onboarding
      console.log("No database found - showing onboarding");

      // Register onboarding route
      router.registerRoute("/onboarding", renderOnboardingPage);

      // Also register other routes (needed after onboarding completes)
      registerAppRoutes();

      // Navigate to onboarding BEFORE initializing router
      // This ensures router.init() renders onboarding instead of trying to render the current route
      router.navigateTo("/onboarding");

      // Initialize router (will handle the /onboarding route)
      router.init();

      document.getElementById("loading").style.display = "none";
      return;
    }

    // Database exists - initialize normally
    console.log("Initializing database...");
    const db = await initDatabase();
    window.appState.db = db;

    // Load user settings
    loadSettings();

    // Register routes
    registerAppRoutes();
    router.registerRoute("/onboarding", renderOnboardingPage);

    // Initialize router
    router.init();

    // Auto-save database periodically
    setInterval(async () => {
      try {
        await saveDatabase();
      } catch (err) {
        console.error("Auto-save failed:", err);
      }
    }, 30000); // Every 30 seconds

    // Save on page unload
    window.addEventListener("beforeunload", async (e) => {
      try {
        await saveDatabase();
      } catch (err) {
        console.error("Save on unload failed:", err);
      }
    });

    // Save on visibility change (when app goes to background)
    document.addEventListener("visibilitychange", async () => {
      if (document.hidden) {
        try {
          await saveDatabase();
        } catch (err) {
          console.error("Save on visibility change failed:", err);
        }
      }
    });
  } catch (error) {
    console.error("Failed to initialize app:", error);
    document.getElementById("app").innerHTML = `
      <div class="page" style="text-align: center; padding: 2rem;">
        <h1>Failed to initialize</h1>
        <p style="color: var(--color-muted); margin-top: 1rem;">
          ${error.message}
        </p>
        <button class="btn-primary" onclick="location.reload()" style="margin-top: 2rem;">
          Reload App
        </button>
      </div>
    `;
  } finally {
    document.getElementById("loading").style.display = "none";
  }
}

function loadSettings() {
  const { query } = window.appState.db
    ? {
        query: (sql, params) => {
          const stmt = window.appState.db.prepare(sql);
          stmt.bind(params || []);
          const results = [];
          while (stmt.step()) {
            results.push(stmt.getAsObject());
          }
          stmt.free();
          return results;
        },
      }
    : { query: () => [] };

  const settings = query("SELECT * FROM UserSettings LIMIT 1");

  if (settings.length > 0) {
    window.appState.settings = settings[0];
  } else {
    window.appState.settings = {
      name: "User"
    };
  }
}

// Start app when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// Export for debugging
window.gymnotesApp = {
  saveDatabase,
  router,
};
